/** Machine Options Middleware
 * Add proper guard functions to Eventless transition
 */

import { eval as expressionEval, parse } from 'expression-eval';
import {
  Condition,
  ConditionPredicate,
  EventObject,
  StateNode,
  TransitionConfigOrTarget,
} from 'xstate';

import { booleanExpressions } from './boolean-expressions';

import { IContext, StateEvent, XStateConfig } from '../types';

type Guard = ConditionPredicate<Record<string, unknown>, EventObject>;

function runCodeWithContext(expression: string, context: Record<string, unknown>) {
  const parsedExpression = parse(expression);
  return expressionEval(parsedExpression, {
    ...context,
    ...booleanExpressions,
  });
}

export const expressionGuard: Guard = (
  context,
  event: EventObject & { data?: Record<string, unknown> },
  meta,
) => {
  const condition = meta.cond.name;
  const ctx = {
    ...context,
    ...event.data,
  };

  return runCodeWithContext(condition, {
    ...ctx,
    context: ctx,
    form: {
      ...ctx,
      'user.data.secondsInStep':
        ctx.stateEnteredDate instanceof Date
          ? (new Date().getTime() - ctx.stateEnteredDate.getTime()) / 1000
          : 0,
    },
  });
};

function findConditionsInTransition(
  transition: TransitionConfigOrTarget<IContext, StateEvent>,
): Condition<IContext, StateEvent>[] {
  if (Array.isArray(transition)) {
    return transition.map((t) => findConditionsInTransition(t)).flat();
  }
  if (!transition || typeof transition === 'string') return [];
  if (transition instanceof StateNode) throw new Error('Unable to handle StateNode');
  if (typeof transition === 'object') {
    return transition.cond ? [transition.cond] : [];
  }
  throw new Error(`Unable to map transition ${transition}`);
}

export function findXstateConditionStrings(config: XStateConfig): string[] {
  return Object.values(config.states ?? {})
    .map((state) => [
      ...findConditionsInTransition(state.always),
      ...Object.values(state.on ?? {}).map((t) =>
        findConditionsInTransition(t as TransitionConfigOrTarget<IContext, StateEvent>),
      ),
    ])
    .flat(2)
    .filter((c: Condition<IContext, StateEvent>): c is string => typeof c === 'string');
}
